/**
 * FieldRenderer Component
 * Renders individual form fields based on their type using Fluent UI
 */

import * as React from 'react';
import {
  Input,
  Textarea,
  Checkbox,
  Dropdown,
  Option,
  Field,
  Text,
  Spinner,
  SpinButton,
  InfoLabel,
  RadioGroup,
  Radio,
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  EyeRegular,
  EyeOffRegular,
  LockClosedRegular,
  EditRegular,
  WarningRegular,
  CameraRegular,
  DismissRegular,
  PhoneRegular,
  AddRegular,
  DeleteRegular,
  ArrowSyncRegular,
  ChevronRightRegular,
  ChevronDownRegular,
} from '@fluentui/react-icons';
import { QRCode } from 'react-qr-code';
import type { FieldDefinition, SelectOption, SubFieldDefinition } from '@structured-request/core';
import { computeFieldValue } from '@structured-request/core';

const useStyles = makeStyles({
  field: {
  },
  repeaterTable: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: tokens.fontSizeBase200,
  },
  repeaterTh: {
    textAlign: 'left' as const,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderBottom: `2px solid ${tokens.colorNeutralStroke1}`,
    fontWeight: tokens.fontWeightSemibold,
    whiteSpace: 'nowrap' as const,
    color: tokens.colorNeutralForeground2,
  },
  repeaterTd: {
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalXS}`,
    verticalAlign: 'top' as const,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  signatureWrapper: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: tokens.spacingVerticalS,
  },
  signatureCanvas: {
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: 'crosshair',
    touchAction: 'none',
    display: 'block',
    width: '100%',
  },
  signaturePreview: {
    maxWidth: '100%',
    maxHeight: '80px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  signatureActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
    flexWrap: 'wrap' as const,
  },
  computedWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  computedLocked: {
    flex: 1,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusSmall,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground2,
    fontFamily: 'inherit',
    fontSize: tokens.fontSizeBase300,
    minHeight: '30px',
    display: 'flex',
    alignItems: 'center',
  },
  description: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    marginTop: tokens.spacingVerticalXS,
  },
  secureToggle: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
  cameraWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  cameraPreview: {
    width: '100%',
    maxWidth: '480px',
    maxHeight: '40vh',
    objectFit: 'contain' as const,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: '#000',
    display: 'block',
  },
  cameraThumbnail: {
    width: '100%',
    maxWidth: '480px',
    maxHeight: '320px',
    objectFit: 'contain',
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  cameraActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
  },
});

// ---------------------------------------------------------------------------
// CameraCapture — opens the device camera and lets the user snap a photo.
// Falls back gracefully when getUserMedia is unavailable (file input shown).
// ---------------------------------------------------------------------------
interface CameraProps {
  disabled?: boolean;
  onCapture: (dataUrl: string, fileName: string) => void;
  currentValue?: string;
  currentFileName?: string;
}

type CaptureStage = 'choice' | 'webcam-live' | 'relay-qr' | 'review';
type FacingMode = 'user' | 'environment';

const CameraCapture: React.FC<CameraProps> = ({ disabled, onCapture, currentValue, currentFileName }) => {
  const [open, setOpen] = React.useState(false);
  const [captureStage, setCaptureStage] = React.useState<CaptureStage>('choice');
  const [captureOrigin, setCaptureOrigin] = React.useState<'webcam' | 'relay' | null>(null);
  const [facingMode, setFacingMode] = React.useState<FacingMode>('environment');
  const [stream, setStream] = React.useState<MediaStream | null>(null);
  const [relayToken, setRelayToken] = React.useState<string | null>(null);
  const [pending, setPending] = React.useState<{ dataUrl: string; fileName: string } | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const styles = useStyles();

  // Stop the webcam on unmount — clear srcObject before stopping tracks so the browser
  // (and AVD camera redirection) releases the hardware indicator promptly.
  React.useEffect(() => {
    return () => {
      const video = videoRef.current;
      if (video) { video.pause(); video.srcObject = null; video.load(); }
      streamRef.current?.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    };
  }, []);

  const stopStream = React.useCallback(() => {
    const video = videoRef.current;
    if (video) { video.pause(); video.srcObject = null; video.load(); }
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setStream(null);
  }, []);

  const startLiveFeed = React.useCallback(async (facing: FacingMode = 'environment') => {
    const s = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 720 } },
    });
    streamRef.current = s;
    setStream(s);
    setPending(null);
  }, []);

  const handleFlipCamera = React.useCallback(async () => {
    const next: FacingMode = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(next);
    const video = videoRef.current;
    if (video) { video.pause(); video.srcObject = null; video.load(); }
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setStream(null);
    try {
      await startLiveFeed(next);
    } catch {
      setError('Could not switch camera.');
    }
  }, [facingMode, startLiveFeed]);

  // Attach webcam stream to video element when entering webcam-live stage
  React.useEffect(() => {
    if (captureStage === 'webcam-live' && stream && videoRef.current) {
      videoRef.current.srcObject = stream;
    }
  }, [captureStage, stream]);

  // Poll relay endpoint every 2s while QR code is displayed
  React.useEffect(() => {
    if (captureStage !== 'relay-qr' || !relayToken) return;
    const poll = async () => {
      try {
        const res = await fetch(`/api/photo-relay/${relayToken}`);
        if (res.status === 200) {
          const data = await res.json();
          if (data.ready) {
            setPending({ dataUrl: data.imageData, fileName: data.fileName });
            setCaptureStage('review');
            setRelayToken(null);
          }
        } else if (res.status === 410) {
          setError('QR code expired. Please try again.');
          setCaptureStage('choice');
          setRelayToken(null);
        }
      } catch {
        // network hiccup — keep polling
      }
    };
    const interval = setInterval(poll, 2000);
    return () => clearInterval(interval);
  }, [captureStage, relayToken]);

  const openDialog = React.useCallback(() => {
    setError(null);
    setPending(null);
    setCaptureStage('choice');
    setCaptureOrigin(null);
    setOpen(true);
  }, []);

  const handleChooseWebcam = React.useCallback(async () => {
    setError(null);
    try {
      await startLiveFeed(facingMode);
      setCaptureOrigin('webcam');
      setCaptureStage('webcam-live');
    } catch {
      setError('Camera unavailable. Please use the file picker below.');
    }
  }, [startLiveFeed, facingMode]);

  const handleChooseRelay = React.useCallback(async () => {
    setError(null);
    try {
      const res = await fetch('/api/photo-relay', { method: 'POST' });
      if (!res.ok) throw new Error('relay init failed');
      const { token } = await res.json();
      setRelayToken(token);
      setCaptureOrigin('relay');
      setCaptureStage('relay-qr');
    } catch {
      setError('Mobile capture unavailable. Please try the webcam or file picker.');
    }
  }, []);

  const handleSnapshot = React.useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')?.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    stopStream();
    setPending({ dataUrl, fileName: `photo_${Date.now()}.jpg` });
    setCaptureStage('review');
  }, [stopStream]);

  const handleRetake = React.useCallback(async () => {
    setPending(null);
    setError(null);
    if (captureOrigin === 'webcam') {
      try {
        await startLiveFeed(facingMode);
        setCaptureStage('webcam-live');
      } catch {
        setError('Camera unavailable.');
        setCaptureStage('choice');
      }
    } else {
      setCaptureStage('choice');
    }
  }, [captureOrigin, startLiveFeed, facingMode]);

  const handleUsePhoto = React.useCallback(() => {
    if (!pending) return;
    onCapture(pending.dataUrl, pending.fileName);
    setPending(null);
    stopStream();
    setOpen(false);
  }, [pending, onCapture, stopStream]);

  const handleClose = React.useCallback(() => {
    if (relayToken) {
      fetch(`/api/photo-relay/${relayToken}`, { method: 'DELETE' }).catch(() => {});
      setRelayToken(null);
    }
    setPending(null);
    stopStream();
    setCaptureStage('choice');
    setOpen(false);
  }, [relayToken, stopStream]);

  const captureUrl = relayToken ? `${window.location.origin}/capture/${relayToken}` : '';

  const dialogTitle =
    captureStage === 'review' ? 'Review Photo' :
    captureStage === 'relay-qr' ? 'Take Photo — Mobile' :
    'Take Photo';

  return (
    <div className={styles.cameraWrapper}>
      <div className={styles.cameraActions}>
        <Button
          icon={<CameraRegular />}
          onClick={openDialog}
          disabled={disabled}
          appearance="secondary"
        >
          {currentValue ? 'Retake Photo' : 'Take Photo'}
        </Button>
        {/* Fallback file input */}
        <input
          type="file"
          accept="image/*"
          capture="environment"
          disabled={disabled}
          style={{ fontSize: '13px' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = (ev) => {
              setPending({ dataUrl: ev.target?.result as string, fileName: file.name });
              setCaptureStage('review');
              setCaptureOrigin('relay');
              setOpen(true);
            };
            reader.readAsDataURL(file);
          }}
        />
      </div>
      {error && <span style={{ color: 'red', fontSize: '12px' }}>{error}</span>}
      {currentValue && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          <img src={currentValue as any} alt="Captured photo" className={styles.cameraThumbnail} />
          {currentFileName && (
            <span style={{ fontSize: '12px', color: 'var(--colorNeutralForeground3)' }}>
              {currentFileName}
            </span>
          )}
        </div>
      )}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) handleClose(); }}>
        <DialogSurface>
          <DialogTitle
            action={
              <Button appearance="subtle" icon={<DismissRegular />} onClick={handleClose} />
            }
          >
            {dialogTitle}
          </DialogTitle>
          <DialogBody>
            <DialogContent>
              {captureStage === 'choice' && (
                <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', padding: '24px 0' }}>
                  <Button
                    appearance="secondary"
                    icon={<CameraRegular />}
                    size="large"
                    style={{ flexDirection: 'column', height: '80px', width: '130px' }}
                    onClick={handleChooseWebcam}
                  >
                    Webcam
                  </Button>
                  <Button
                    appearance="secondary"
                    icon={<PhoneRegular />}
                    size="large"
                    style={{ flexDirection: 'column', height: '80px', width: '130px' }}
                    onClick={handleChooseRelay}
                  >
                    Mobile Device
                  </Button>
                </div>
              )}
              {captureStage === 'webcam-live' && (
                // eslint-disable-next-line jsx-a11y/media-has-caption
                <video ref={videoRef} autoPlay playsInline muted className={styles.cameraPreview} />
              )}
              {captureStage === 'relay-qr' && captureUrl && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', padding: '16px 0' }}>
                  <QRCode value={captureUrl} size={200} />
                  <span style={{ fontSize: '13px', color: 'var(--colorNeutralForeground2)', textAlign: 'center' }}>
                    Scan with your phone to take the photo
                  </span>
                  <span style={{ fontSize: '11px', color: 'var(--colorNeutralForeground3)', textAlign: 'center', wordBreak: 'break-all' }}>
                    {captureUrl}
                  </span>
                  <span style={{ fontSize: '12px', color: 'var(--colorNeutralForeground3)' }}>
                    Waiting for photo...
                  </span>
                </div>
              )}
              {captureStage === 'review' && pending && (
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                <img src={pending.dataUrl as any} alt="Preview" className={styles.cameraPreview} style={{ display: 'block' }} />
              )}
            </DialogContent>
          </DialogBody>
          <DialogActions>
            {captureStage === 'choice' && (
              <Button appearance="subtle" onClick={handleClose}>Cancel</Button>
            )}
            {captureStage === 'webcam-live' && (
              <>
                <Button appearance="primary" icon={<CameraRegular />} onClick={handleSnapshot}>
                  Capture
                </Button>
                <Button
                  appearance="secondary"
                  icon={<ArrowSyncRegular />}
                  onClick={handleFlipCamera}
                  title={facingMode === 'environment' ? 'Switch to front camera' : 'Switch to rear camera'}
                >
                  Flip
                </Button>
                <Button appearance="subtle" onClick={handleClose}>Cancel</Button>
              </>
            )}
            {captureStage === 'relay-qr' && (
              <Button appearance="subtle" onClick={handleClose}>Cancel</Button>
            )}
            {captureStage === 'review' && (
              <>
                <Button appearance="primary" onClick={handleUsePhoto}>Use Photo</Button>
                <Button appearance="secondary" icon={<CameraRegular />} onClick={handleRetake}>
                  Retake
                </Button>
                <Button appearance="subtle" onClick={handleClose}>Cancel</Button>
              </>
            )}
          </DialogActions>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

// ---------------------------------------------------------------------------
// useBlobUrl — resolves a { blobName } reference to a SAS URL via /api/media-sas
// Returns the dataUrl unchanged if it's already a data: URI or http URL.
// ---------------------------------------------------------------------------
function useBlobUrl(value: unknown): { url: string | undefined; loading: boolean } {
  const hasBlobName = Boolean((value as any)?.blobName);

  const [url, setUrl] = React.useState<string | undefined>(() => {
    if (typeof value === 'string') return value;
    const v = value as any;
    if (v?.dataUrl) return v.dataUrl;
    return undefined;
  });
  const [loading, setLoading] = React.useState(hasBlobName);

  React.useEffect(() => {
    const v = value as any;
    if (!v?.blobName) return;
    setLoading(true);
    let cancelled = false;
    fetch(`/api/media-sas?blobName=${encodeURIComponent(v.blobName)}`)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (!cancelled) {
          if (data?.url) setUrl(data.url);
          setLoading(false);
        }
      })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [(value as any)?.blobName]);

  return { url, loading };
}

// ---------------------------------------------------------------------------
// SignatureCapture — canvas ink capture with mobile relay and file upload fallback
// ---------------------------------------------------------------------------
interface SignatureCaptureProps {
  value?: string;
  onChange: (dataUrl: string | undefined) => void;
  disabled?: boolean;
}

const SignatureCapture: React.FC<SignatureCaptureProps> = ({ value, onChange, disabled }) => {
  const styles = useStyles();
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const isDrawing = React.useRef(false);
  const lastPos = React.useRef<{ x: number; y: number } | null>(null);
  // Use a separate state flag to drive canvas vs preview rendering,
  // so Clear immediately shows the canvas without waiting for value prop to reset.
  const [showCanvas, setShowCanvas] = React.useState(!value);
  const [relayToken, setRelayToken] = React.useState<string | null>(null);
  const [relayError, setRelayError] = React.useState<string | null>(null);

  // When value changes externally (e.g. prefill), sync the canvas visibility
  React.useEffect(() => {
    setShowCanvas(!value);
  }, [value]);

  // Attach touch listeners as non-passive so preventDefault works (suppresses scroll while drawing)
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || disabled || !showCanvas) return;

    const getPosFromTouch = (e: TouchEvent) => {
      const rect = canvas.getBoundingClientRect();
      const t = e.touches[0];
      return {
        x: (t.clientX - rect.left) * (canvas.width / rect.width),
        y: (t.clientY - rect.top) * (canvas.height / rect.height),
      };
    };

    const onTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      isDrawing.current = true;
      lastPos.current = getPosFromTouch(e);
    };
    const onTouchMove = (e: TouchEvent) => {
      if (!isDrawing.current) return;
      e.preventDefault();
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const pos = getPosFromTouch(e);
      ctx.beginPath();
      ctx.moveTo(lastPos.current!.x, lastPos.current!.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
      lastPos.current = pos;
    };
    const onTouchEnd = () => {
      if (!isDrawing.current) return;
      isDrawing.current = false;
      lastPos.current = null;
      onChange(canvas.toDataURL('image/png'));
    };

    canvas.addEventListener('touchstart', onTouchStart, { passive: false });
    canvas.addEventListener('touchmove', onTouchMove, { passive: false });
    canvas.addEventListener('touchend', onTouchEnd);
    return () => {
      canvas.removeEventListener('touchstart', onTouchStart);
      canvas.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('touchend', onTouchEnd);
    };
  }, [disabled, showCanvas, onChange]);

  const getPosFromMouse = (e: React.MouseEvent, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (disabled) return;
    isDrawing.current = true;
    lastPos.current = getPosFromMouse(e, canvasRef.current!);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDrawing.current || disabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const pos = getPosFromMouse(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current!.x, lastPos.current!.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    lastPos.current = pos;
  };

  const handleMouseUp = () => {
    if (!isDrawing.current) return;
    isDrawing.current = false;
    lastPos.current = null;
    const canvas = canvasRef.current;
    if (canvas) onChange(canvas.toDataURL('image/png'));
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    onChange(undefined);
    setShowCanvas(true);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      onChange(ev.target?.result as string);
      setShowCanvas(false);
    };
    reader.readAsDataURL(file);
  };

  const handleMobileRelay = async () => {
    setRelayError(null);
    try {
      const res = await fetch('/api/photo-relay', { method: 'POST' });
      if (!res.ok) throw new Error('relay init failed');
      const { token } = await res.json();
      setRelayToken(token);
    } catch {
      setRelayError('Mobile signature capture unavailable. Please draw or upload a file.');
    }
  };

  const handleCancelRelay = () => {
    if (relayToken) fetch(`/api/photo-relay/${relayToken}`, { method: 'DELETE' }).catch(() => {});
    setRelayToken(null);
    setRelayError(null);
  };

  // Poll relay endpoint — driven by relayToken only, no relayStage dependency
  React.useEffect(() => {
    if (!relayToken) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`/api/photo-relay/${relayToken}`);
        if (res.status === 200) {
          const data = await res.json();
          if (data.ready) {
            clearInterval(interval);
            setRelayToken(null);
            onChange(data.imageData);
            setShowCanvas(false);
          }
        } else if (res.status === 410) {
          clearInterval(interval);
          setRelayToken(null);
          setRelayError('QR code expired. Please try again.');
        }
      } catch { /* network hiccup — keep polling */ }
    }, 2000);
    return () => clearInterval(interval);
  }, [relayToken, onChange]);

  const signatureUrl = relayToken ? `${window.location.origin}/signature/${relayToken}` : '';

  if (disabled) {
    return value
      ? <img src={value} alt="Signature" className={styles.signaturePreview} />
      : <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>No signature provided</Text>;
  }

  // Relay QR view
  if (relayToken) {
    return (
      <div className={styles.signatureWrapper}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
          <QRCode value={signatureUrl} size={180} />
          <Text size={200} style={{ textAlign: 'center' }}>Scan with your phone to sign</Text>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Waiting for signature...</Text>
          <Button appearance="subtle" size="small" onClick={handleCancelRelay}>Cancel</Button>
        </div>
      </div>
    );
  }

  // Preview view — after signing or uploading
  if (!showCanvas && value) {
    return (
      <div className={styles.signatureWrapper}>
        <img src={value} alt="Signature" className={styles.signaturePreview} />
        <div className={styles.signatureActions}>
          <Button appearance="subtle" size="small" icon={React.createElement(DismissRegular)} onClick={handleClear}>
            Clear &amp; Redo
          </Button>
        </div>
      </div>
    );
  }

  // Canvas draw view
  return (
    <div className={styles.signatureWrapper}>
      <canvas
        ref={canvasRef}
        width={600}
        height={150}
        className={styles.signatureCanvas}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      />
      <div className={styles.signatureActions}>
        <Button appearance="subtle" size="small" icon={React.createElement(DismissRegular)} onClick={handleClear}>Clear</Button>
        <Button appearance="subtle" size="small" icon={React.createElement(PhoneRegular)} onClick={handleMobileRelay}>Sign on Mobile</Button>
        <label style={{ cursor: 'pointer', fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground2 }}>
          Upload image
          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileUpload} />
        </label>
      </div>
      {relayError && <Text size={200} style={{ color: tokens.colorStatusDangerForeground1 }}>{relayError}</Text>}
      <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>Draw your signature above, sign on your mobile device, or upload an image.</Text>
    </div>
  );
};

// ---------------------------------------------------------------------------
// RepeaterField — master/detail list with expand-in-place row editing
// Collapsed: shows a summary line of key field values
// Expanded: full 2-column grid form for all sub-fields
// ---------------------------------------------------------------------------
interface RepeaterFieldProps {
  field: FieldDefinition;
  rows: Record<string, unknown>[];
  onChange: (rows: Record<string, unknown>[]) => void;
  disabled?: boolean;
  errors?: Record<string, string>;
}

const RepeaterField: React.FC<RepeaterFieldProps> = ({ field, rows, onChange, disabled, errors = {} }) => {
  const subFields: SubFieldDefinition[] = field.subFields ?? [];
  const [expandedIdx, setExpandedIdx] = React.useState<number | null>(null);

  const inputStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '5px 8px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusSmall,
    background: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
  };

  const addRow = () => {
    const empty: Record<string, unknown> = {};
    subFields.forEach((sf) => { empty[sf.name] = ''; });
    const newIdx = rows.length;
    onChange([...rows, empty]);
    setExpandedIdx(newIdx);
  };

  const removeRow = (idx: number) => {
    onChange(rows.filter((_, i) => i !== idx));
    if (expandedIdx === idx) setExpandedIdx(null);
    else if (expandedIdx !== null && expandedIdx > idx) setExpandedIdx(expandedIdx - 1);
  };

  const updateCell = (rowIdx: number, colName: string, value: unknown) => {
    onChange(rows.map((row, i) => i === rowIdx ? { ...row, [colName]: value } : row));
  };

  // Build a readable summary from the first 3 non-empty text-like values
  const rowSummary = (row: Record<string, unknown>) => {
    const parts: string[] = [];
    for (const sf of subFields) {
      if (parts.length >= 3) break;
      const v = row[sf.name];
      if (v !== undefined && v !== null && v !== '' && sf.type !== 'checkbox') {
        const label = sf.type === 'select' || sf.type === 'radio'
          ? (sf.options?.find((o) => o.value === String(v))?.label ?? String(v))
          : String(v);
        parts.push(label);
      }
    }
    return parts.length ? parts.join('  ·  ') : 'New row';
  };

  const hasRowError = (rowIdx: number) =>
    subFields.some((sf) => errors[`${field.name}[${rowIdx}].${sf.name}`]);

  const renderSubFieldInput = (sf: SubFieldDefinition, rowIdx: number) => {
    const val = rows[rowIdx]?.[sf.name] ?? '';
    switch (sf.type) {
      case 'select':
      case 'radio':
        return (
          <select value={String(val)} onChange={(e) => updateCell(rowIdx, sf.name, e.target.value)} disabled={disabled} style={inputStyle}>
            <option value="">— select —</option>
            {(sf.options ?? []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        );
      case 'checkbox':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS, paddingTop: tokens.spacingVerticalXS }}>
            <input type="checkbox" checked={Boolean(val)} onChange={(e) => updateCell(rowIdx, sf.name, e.target.checked)} disabled={disabled} />
            <Text size={200}>{val ? 'Yes' : 'No'}</Text>
          </div>
        );
      case 'textarea':
        return <textarea value={String(val)} onChange={(e) => updateCell(rowIdx, sf.name, e.target.value)} placeholder={sf.placeholder} disabled={disabled} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />;
      default:
        return <input type={sf.type === 'email' ? 'email' : sf.type === 'number' ? 'number' : sf.type === 'date' ? 'date' : 'text'} value={String(val)} onChange={(e) => updateCell(rowIdx, sf.name, e.target.value)} placeholder={sf.placeholder} disabled={disabled} style={inputStyle} />;
    }
  };

  if (subFields.length === 0) {
    return <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>No columns defined — edit this field to add columns.</Text>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, width: '100%' }}>
      {rows.length === 0 && (
        <div style={{ padding: `${tokens.spacingVerticalM} 0`, color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 }}>
          No rows yet — click Add Row to begin.
        </div>
      )}

      {rows.map((row, rowIdx) => {
        const isExpanded = expandedIdx === rowIdx;
        const hasError = hasRowError(rowIdx);
        return (
          <div
            key={rowIdx}
            style={{
              border: `1px solid ${isExpanded ? tokens.colorBrandStroke1 : hasError ? tokens.colorStatusDangerBorder1 : tokens.colorNeutralStroke2}`,
              borderRadius: tokens.borderRadiusMedium,
              overflow: 'hidden',
              backgroundColor: tokens.colorNeutralBackground1,
            }}
          >
            {/* Collapsed header row — click to expand */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: tokens.spacingHorizontalS,
                padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
                cursor: 'pointer',
                backgroundColor: isExpanded ? tokens.colorNeutralBackground2 : 'transparent',
                userSelect: 'none',
              }}
              onClick={() => setExpandedIdx(isExpanded ? null : rowIdx)}
            >
              {isExpanded
                ? React.createElement(ChevronDownRegular, { style: { fontSize: '14px', color: tokens.colorNeutralForeground3, flexShrink: 0 } })
                : React.createElement(ChevronRightRegular, { style: { fontSize: '14px', color: tokens.colorNeutralForeground3, flexShrink: 0 } })
              }
              <Text
                size={200}
                style={{
                  flex: 1,
                  color: hasError ? tokens.colorStatusDangerForeground1 : tokens.colorNeutralForeground1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                <Text size={200} weight="semibold" style={{ marginRight: tokens.spacingHorizontalXS }}>
                  {rowIdx + 1}.
                </Text>
                {rowSummary(row)}
                {hasError && <Text size={200} style={{ color: tokens.colorStatusDangerForeground1, marginLeft: tokens.spacingHorizontalS }}>⚠ incomplete</Text>}
              </Text>
              {!disabled && (
                <Button
                  icon={<DeleteRegular />}
                  appearance="subtle"
                  size="small"
                  title="Remove row"
                  onClick={(e) => { e.stopPropagation(); removeRow(rowIdx); }}
                />
              )}
            </div>

            {/* Expanded edit form */}
            {isExpanded && (
              <div
                style={{
                  padding: tokens.spacingVerticalM,
                  borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
                }}
              >
                {subFields.map((sf) => {
                  const errKey = `${field.name}[${rowIdx}].${sf.name}`;
                  return (
                    <div key={sf.name} style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS }}>
                      <Text size={200} weight="semibold">
                        {sf.label}
                        {sf.required && <span style={{ color: tokens.colorStatusDangerForeground1 }}> *</span>}
                      </Text>
                      {renderSubFieldInput(sf, rowIdx)}
                      {errors[errKey] && (
                        <Text size={100} style={{ color: tokens.colorStatusDangerForeground1 }}>{errors[errKey]}</Text>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {!disabled && (
        <Button
          icon={<AddRegular />}
          appearance="subtle"
          size="small"
          onClick={addRow}
          style={{ alignSelf: 'flex-start', marginTop: tokens.spacingVerticalXS }}
        >
          Add Row
        </Button>
      )}
    </div>
  );
};

export interface FieldRendererProps {
  field: FieldDefinition;
  value: unknown;
  onChange: (name: string, value: unknown) => void;
  error?: string;
  /** Full errors map — used by repeater to show per-row/cell errors */
  allErrors?: Record<string, string>;
  disabled?: boolean;
  /** All current form values — required for computed field resolution */
  allValues?: Record<string, unknown>;
  /** Context for presigned file uploads */
  uploadContext?: { mspId: string; responseId: string };
}

export const FieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  value,
  onChange,
  error,
  allErrors = {},
  disabled = false,
  allValues = {},
  uploadContext,
}) => {
  const styles = useStyles();
  const [showSecure, setShowSecure] = React.useState(false);
  const [computedOverridden, setComputedOverridden] = React.useState(
    // Pre-populate override state if a non-computed value is already stored
    field.type === 'computed' && value !== undefined && value !== ''
  );
  const [showWarningDialog, setShowWarningDialog] = React.useState(false);

  const handleChange = (newValue: unknown) => {
    onChange(field.name, newValue);
  };

  const renderInput = () => {
    switch (field.type) {
      case 'text':
      case 'email':
      case 'phone':
        if (field.isSecure) {
          return (
            <div className={styles.secureToggle}>
              <Input
                value={String(value ?? '')}
                onChange={(_, data) => handleChange(data.value)}
                placeholder={field.placeholder}
                disabled={disabled}
                type={showSecure ? 'text' : 'password'}
                style={{ flex: 1 }}
                contentAfter={
                  <button
                    type="button"
                    onClick={() => setShowSecure(!showSecure)}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 4 }}
                    aria-label={showSecure ? 'Hide value' : 'Show value'}
                  >
                    {showSecure ? <EyeOffRegular /> : <EyeRegular />}
                  </button>
                }
              />
            </div>
          );
        }
        return (
          <Input
            value={String(value ?? '')}
            onChange={(_, data) => handleChange(data.value)}
            placeholder={field.placeholder}
            disabled={disabled}
            type={field.type === 'email' ? 'email' : 'text'}
            style={{ width: '100%' }}
          />
        );

      case 'textarea':
        return (
          <Textarea
            value={String(value ?? '')}
            onChange={(_, data) => handleChange(data.value)}
            placeholder={field.placeholder}
            disabled={disabled}
            resize="vertical"
            rows={4}
            style={{ width: '100%' }}
          />
        );

      case 'number':
        return (
          <SpinButton
            value={value !== undefined && value !== '' ? Number(value) : undefined}
            onChange={(_, data) => handleChange(data.value)}
            disabled={disabled}
            min={field.validation?.min}
            max={field.validation?.max}
            style={{ width: '100%' }}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={String(value ?? '')}
            onChange={(e) => handleChange(e.target.value)}
            disabled={disabled}
            placeholder={field.placeholder || 'Select a date'}
            style={{ width: '100%', padding: '4px 8px', fontSize: '14px', boxSizing: 'border-box' }}
          />
        );

      case 'datetime': {
        return (
          <input
            type="datetime-local"
            value={String(value ?? '')}
            onChange={(e) => handleChange(e.target.value)}
            disabled={disabled}
            style={{ width: '100%', padding: '4px 8px', fontSize: '14px', boxSizing: 'border-box' }}
          />
        );
      }

      case 'time':
        return (
          <input
            type="time"
            value={String(value ?? '')}
            onChange={(e) => handleChange(e.target.value)}
            disabled={disabled}
            style={{ width: '100%', padding: '4px 8px', fontSize: '14px', boxSizing: 'border-box' }}
          />
        );

      case 'checkbox':
        return (
          <Checkbox
            checked={Boolean(value)}
            onChange={(_, data) => handleChange(data.checked)}
            disabled={disabled}
            label={field.description || field.label}
          />
        );

      case 'radio':
        return (
          <RadioGroup
            value={String(value ?? '')}
            onChange={(_, data) => handleChange(data.value)}
            disabled={disabled}
          >
            {(field.options ?? []).map((opt: SelectOption) => (
              <Radio key={opt.value} value={opt.value} label={opt.label} />
            ))}
          </RadioGroup>
        );

      case 'select':
        return (
          <Dropdown
            value={String(value ?? '')}
            selectedOptions={value ? [String(value)] : []}
            onOptionSelect={(_, data) => handleChange(data.optionValue)}
            disabled={disabled}
            placeholder={field.placeholder || 'Select an option'}
            style={{ width: '100%', minWidth: 0 }}
          >
            {(field.options ?? []).map((opt: SelectOption) => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Dropdown>
        );

      case 'multiselect':
        const selectedValues = Array.isArray(value) ? value.map(String) : [];
        return (
          <Dropdown
            multiselect
            selectedOptions={selectedValues}
            onOptionSelect={(_, data) => handleChange(data.selectedOptions)}
            disabled={disabled}
            placeholder={field.placeholder || 'Select options'}
            style={{ width: '100%', minWidth: 0 }}
          >
            {(field.options ?? []).map((opt: SelectOption) => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Dropdown>
        );

      case 'file': {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const { url: resolvedFileUrl, loading: fileLoading } = useBlobUrl(value);
        const fileVal = value as { blobName?: string; fileName?: string } | null | undefined;
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [fileUploading, setFileUploading] = React.useState(false);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [fileUploadError, setFileUploadError] = React.useState<string | null>(null);

        const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
          const file = e.target.files?.[0];
          if (!file) return;
          setFileUploadError(null);

          if (!uploadContext) {
            // Fallback: no upload context — store filename only
            handleChange(file.name);
            return;
          }

          setFileUploading(true);
          try {
            const res = await fetch('/api/media-upload', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                mspId: uploadContext.mspId,
                responseId: uploadContext.responseId,
                fieldName: field.name,
                fileName: file.name,
                contentType: file.type || 'application/octet-stream',
                fileSizeMB: file.size / (1024 * 1024),
              }),
            });

            if (!res.ok) {
              const err = await res.json().catch(() => ({ error: 'Upload failed' }));
              setFileUploadError(err.error ?? 'Upload failed');
              return;
            }

            const { sasUrl, blobName, fileName: safeName } = await res.json();

            // Upload file bytes directly to blob storage
            const putRes = await fetch(sasUrl, {
              method: 'PUT',
              headers: {
                'Content-Type': file.type || 'application/octet-stream',
                'x-ms-blob-type': 'BlockBlob',
              },
              body: file,
            });

            if (!putRes.ok) {
              setFileUploadError('File upload to storage failed. Please try again.');
              return;
            }

            handleChange({ blobName, fileName: safeName ?? file.name });
          } catch {
            setFileUploadError('Upload failed. Please check your connection and try again.');
          } finally {
            setFileUploading(false);
          }
        };

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS }}>
            {fileLoading && <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Loading file...</Text>}
            {!fileLoading && fileVal?.blobName && (
              <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
                {resolvedFileUrl
                  ? <a href={resolvedFileUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: tokens.fontSizeBase200 }}>{fileVal.fileName ?? 'Download file'}</a>
                  : <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>{fileVal.fileName ?? 'File uploaded'}</Text>
                }
              </div>
            )}
            {!fileLoading && !fileVal?.blobName && fileVal?.fileName && (
              <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>{fileVal.fileName}</Text>
            )}
            {fileUploading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
                <Spinner size="tiny" />
                <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Uploading...</Text>
              </div>
            )}
            {fileUploadError && <Text size={200} style={{ color: tokens.colorStatusDangerForeground1 }}>{fileUploadError}</Text>}
            {!disabled && !fileUploading && (
              <input
                type="file"
                onChange={handleFileChange}
                disabled={disabled}
                accept={field.validation?.allowedFileTypes?.join(',')}
                style={{ display: 'block', width: '100%' }}
              />
            )}
          </div>
        );
      }

      case 'scan': {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const { url: resolvedScanUrl, loading: scanLoading } = useBlobUrl(value);
        const scanVal = value as { blobName?: string; fileName?: string } | null | undefined;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS }}>
            {scanLoading && <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Loading scan...</Text>}
            {!scanLoading && resolvedScanUrl && (
              <a href={resolvedScanUrl} target="_blank" rel="noopener noreferrer">
                <img src={resolvedScanUrl} alt={scanVal?.fileName ?? 'scan'} style={{ maxWidth: '100%', maxHeight: '120px', objectFit: 'contain', border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusSmall, display: 'block' }} />
              </a>
            )}
            {!scanLoading && !resolvedScanUrl && scanVal?.fileName && (
              <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>{scanVal.fileName}</Text>
            )}
            {!disabled && (
              <input
                type="file"
                accept={field.validation?.allowedFileTypes?.join(',') ?? 'image/*'}
                capture="environment"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = (ev) => handleChange({ dataUrl: ev.target?.result as string, fileName: file.name });
                  reader.readAsDataURL(file);
                }}
                disabled={disabled}
                style={{ display: 'block', width: '100%' }}
              />
            )}
          </div>
        );
      }

      case 'photo': {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const { url: resolvedPhotoUrl, loading: photoLoading } = useBlobUrl(value);
        const photoVal = value as { dataUrl?: string; fileName?: string; blobName?: string } | null | undefined;
        const photoFileName = photoVal?.fileName;
        if (photoLoading) return <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Loading photo...</Text>;
        return (
          <CameraCapture
            disabled={disabled}
            currentValue={resolvedPhotoUrl}
            currentFileName={photoFileName}
            onCapture={(dataUrl, fileName) => {
              handleChange({ dataUrl, fileName });
            }}
          />
        );
      }

      case 'signature': {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const { url: resolvedSigUrl, loading: sigLoading } = useBlobUrl(value);
        if (sigLoading) return <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Loading signature...</Text>;
        return (
          <SignatureCapture
            value={resolvedSigUrl}
            onChange={(dataUrl) => handleChange(dataUrl)}
            disabled={disabled}
          />
        );
      }

      case 'spacer':
        return null;

      case 'computed': {
        const template = field.computedTemplate ?? '';
        const computedValue = template ? computeFieldValue(template, allValues) : '';
        const canOverride = field.allowComputedOverride === true;
        const displayValue = computedOverridden ? String(value ?? '') : computedValue;

        if (canOverride && computedOverridden) {
          return (
            <div className={styles.computedWrapper}>
              <Input
                value={displayValue}
                onChange={(_, data) => handleChange(data.value)}
                placeholder={field.placeholder}
                disabled={disabled}
                style={{ flex: 1 }}
                contentAfter={
                  <button
                    type="button"
                    onClick={() => {
                      setComputedOverridden(false);
                      handleChange(undefined);
                    }}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 4 }}
                    aria-label="Restore computed value"
                    title="Restore computed value"
                  >
                    <LockClosedRegular />
                  </button>
                }
              />
            </div>
          );
        }

        const activateOverride = () => {
          setComputedOverridden(true);
          handleChange(displayValue);
        };

        return (
          <>
            <div className={styles.computedWrapper}>
              <div className={styles.computedLocked}>
                {displayValue || <span style={{ color: tokens.colorNeutralForeground4 }}>{field.placeholder || '(computed)'}</span>}
              </div>
              {canOverride && !disabled && (
                <button
                  type="button"
                  onClick={() => {
                    if (field.computedOverrideWarning) {
                      setShowWarningDialog(true);
                    } else {
                      activateOverride();
                    }
                  }}
                  style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 4, flexShrink: 0 }}
                  aria-label="Override computed value"
                  title="Override computed value"
                >
                  <EditRegular style={{ color: tokens.colorNeutralForeground3 }} />
                </button>
              )}
            </div>
            <Dialog open={showWarningDialog} onOpenChange={(_, data) => setShowWarningDialog(data.open)}>
              <DialogSurface>
                <DialogBody>
                  <DialogTitle
                    action={null}
                    style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}
                  >
                    <WarningRegular style={{ color: tokens.colorStatusWarningForeground1, fontSize: 20 }} />
                    Override {field.label}?
                  </DialogTitle>
                  <DialogContent style={{ whiteSpace: 'pre-wrap' }}>
                    {field.computedOverrideWarning}
                  </DialogContent>
                  <DialogActions>
                    <Button appearance="subtle" onClick={() => setShowWarningDialog(false)}>
                      Cancel
                    </Button>
                    <Button
                      appearance="primary"
                      onClick={() => {
                        setShowWarningDialog(false);
                        activateOverride();
                      }}
                    >
                      Continue
                    </Button>
                  </DialogActions>
                </DialogBody>
              </DialogSurface>
            </Dialog>
          </>
        );
      }

      case 'repeater': {
        const rows = Array.isArray(value) ? (value as Record<string, unknown>[]) : [];
        return (
          <RepeaterField
            field={field}
            rows={rows}
            onChange={(updated) => handleChange(updated)}
            disabled={disabled}
            errors={allErrors}
          />
        );
      }
    }
  };

  // Spacer fields render nothing — they occupy grid space only
  if (field.type === 'spacer') {
    return null;
  }

  // Checkbox, radio, repeater, and signature are rendered without the outer Field label wrapper
  if (field.type === 'checkbox' || field.type === 'radio' || field.type === 'repeater' || field.type === 'signature') {
    return (
      <div className={styles.field}>
        {(field.type === 'repeater' || field.type === 'signature') && (
          <div style={{ marginBottom: tokens.spacingVerticalXS }}>
            <Text size={300} weight="semibold">{field.label}{field.required && <span style={{ color: tokens.colorStatusDangerForeground1 }}> *</span>}</Text>
            {field.description && <div style={{ color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 }}>{field.description}</div>}
          </div>
        )}
        {renderInput()}
        {error && (
          <Field validationState="error" validationMessage={error} />
        )}
      </div>
    );
  }

  return (
    <Field
      className={styles.field}
      label={
        field.description ? (
          <InfoLabel info={field.description}>
            {field.label}
          </InfoLabel>
        ) : (
          field.label
        )
      }
      validationState={error ? 'error' : undefined}
      validationMessage={error}
      required={field.required}
    >
      {renderInput()}
    </Field>
  );
};

export default FieldRenderer;
